import { useScrollToTop } from "@/hooks/useScrollToTop";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { MerchantReturnPolicySchema } from "@/components/MerchantReturnPolicySchema";

const ReturnPolicy = () => {
  useScrollToTop();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Return & Refund Policy | Aureum VII Trading"
        description="Return and refund policy for Aureum VII Trading: EU consumer rights, the 14-day return window and warranty cover for smart toilets and bath products."
        keywords="return policy, refund policy, smart toilet returns, EU consumer rights, warranty"
        url="/return-policy"
      />
      <MerchantReturnPolicySchema />
      <Navigation />
      
      <main className="container mx-auto px-4 py-20 max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">Return & Refund Policy</h1>
        
        <Card className="p-8 space-y-8">
          <section>
            <p className="text-muted-foreground mb-6">
              Last Updated: {new Date().toLocaleDateString('en-GB')}
            </p>
            <p className="mb-6">
              At Aureum VII Trading, we are committed to your complete satisfaction with our luxury smart toilets, 
              bidets, and wellness bathroom technology. This Return & Refund Policy outlines your rights and our 
              procedures in accordance with EU consumer protection regulations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">1. Return Eligibility Period</h2>
            <p className="mb-4">
              In accordance with EU Directive 2011/83/EU, you have the right to cancel your order and return 
              products within <strong>14 calendar days</strong> from the date of delivery without giving any reason.
            </p>
            <p>
              To exercise your right of withdrawal, you must inform us of your decision by email to 
              <a href="mailto:info@aureum7trading.com" className="text-primary hover:underline ml-1">
                info@aureum7trading.com
              </a> or by phone at +34 642 278 939.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Return Conditions</h2>
            <p className="mb-4">
              Returns are accepted only under the following conditions:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Products must be <strong>unused, uninstalled, and in original packaging</strong></li>
              <li>All accessories, manuals, and components must be included</li>
              <li>Products must be in resalable condition with no damage or modifications</li>
              <li>Smart toilets and bidets that have been installed cannot be returned due to hygiene regulations</li>
              <li>Custom orders, special configurations, or made-to-order items are non-returnable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. Items Not Eligible for Return</h2>
            <p className="mb-4">The following items cannot be returned:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Products damaged during installation by third parties or customers</li>
              <li>Products altered, modified, or installed in any way</li>
              <li>Products showing signs of use, wear, or connection to plumbing systems</li>
              <li>Items purchased during final sale or clearance promotions (clearly marked as non-returnable)</li>
              <li>Electronic components with broken seals or evidence of usage</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">4. Return Process</h2>
            <ol className="list-decimal pl-6 space-y-3">
              <li>
                <strong>Contact Us:</strong> Email info@aureum7trading.com with your order number, 
                reason for return, and photos if applicable
              </li>
              <li>
                <strong>Receive Authorization:</strong> We will review your request within 48 hours 
                and provide a Return Merchandise Authorization (RMA) number
              </li>
              <li>
                <strong>Package Securely:</strong> Repack the product in its original packaging with 
                all included materials
              </li>
              <li>
                <strong>Ship Back:</strong> Send the product to the address provided with your RMA. 
                We recommend using insured, tracked shipping
              </li>
              <li>
                <strong>Inspection:</strong> Upon receipt, we will inspect the product within 5 business days
              </li>
              <li>
                <strong>Refund:</strong> If approved, refunds will be processed within 14 days to your original 
                payment method
              </li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">5. Return Shipping Costs</h2>
            <p className="mb-4">
              <strong>Customer-Initiated Returns:</strong> If you are returning a product for reasons other 
              than defect or error on our part, you are responsible for return shipping costs.
            </p>
            <p className="mb-4">
              <strong>Defective or Incorrect Items:</strong> If the product is defective, damaged in transit, 
              or we sent the wrong item, we will cover all return shipping costs and provide a prepaid return label.
            </p>
            <p>
              For large or heavy items (smart toilets, bathtubs), please contact us before shipping for freight 
              arrangement and cost discussion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">6. Refund Process</h2>
            <p className="mb-4">
              Once your return is received and inspected, we will send you an email notification of approval 
              or rejection of your refund.
            </p>
            <p className="mb-4">
              <strong>Approved Refunds:</strong> Your refund will be processed within 14 calendar days, 
              and a credit will automatically be applied to your original method of payment.
            </p>
            <p className="mb-4">
              <strong>Partial Refunds:</strong> In some cases, only partial refunds may be granted:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Products with obvious signs of use</li>
              <li>Products not in original condition, damaged, or missing parts not due to our error</li>
              <li>Products returned more than 14 days after delivery</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">7. Warranty & Defective Products</h2>
            <p className="mb-4">
              All Aureum VII Trading products come with a <strong>manufacturer's warranty</strong> covering 
              defects in materials and workmanship:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Standard Warranty:</strong> 2-3 years depending on product line</li>
              <li><strong>Electronic Components:</strong> 1-2 years warranty</li>
              <li><strong>Mechanical Parts:</strong> 3-5 years warranty (varies by model)</li>
            </ul>
            <p className="mb-4">
              Warranty does not cover:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Normal wear and tear</li>
              <li>Damage caused by improper installation or misuse</li>
              <li>Modifications or repairs by unauthorized technicians</li>
              <li>Damage from water quality issues, power surges, or external factors</li>
            </ul>
            <p className="mt-4">
              For warranty claims, please contact us with proof of purchase, photos, and a description 
              of the issue. We will arrange repair, replacement, or refund as appropriate.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">8. Exchanges</h2>
            <p className="mb-4">
              We only replace items if they are defective or damaged. If you need to exchange a product 
              for the same item, contact us at info@aureum7trading.com with your order details.
            </p>
            <p>
              For different models or upgrades, you must complete a return and place a new order.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">9. Cancellations</h2>
            <p className="mb-4">
              Orders can be cancelled free of charge within 24 hours of placement. After this period, 
              cancellation may incur a restocking fee if the product has already been prepared for shipment.
            </p>
            <p>
              Custom orders cannot be cancelled once production has begun.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">10. Installation-Related Issues</h2>
            <p className="mb-4">
              Aureum VII Trading is not responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Damage caused during installation by third-party contractors or customers</li>
              <li>Installation errors or incompatibility with existing plumbing systems</li>
              <li>Additional costs for professional installation or removal</li>
            </ul>
            <p className="mt-4">
              We strongly recommend professional installation by certified plumbers. Installation guides 
              and technical support are available upon request.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">11. EU Consumer Rights</h2>
            <p className="mb-4">
              Under EU law (Directive 2011/83/EU), consumers purchasing goods online have specific rights:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Right to withdraw from purchase within 14 days</li>
              <li>Right to receive refund within 14 days of return</li>
              <li>Right to receive goods as described</li>
              <li>Right to goods that are fit for purpose</li>
              <li>Right to goods that match samples or descriptions</li>
            </ul>
            <p className="mt-4">
              These rights do not affect your statutory rights under national consumer protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">12. Contact Information</h2>
            <p className="mb-2">
              For questions about returns, refunds, or warranty claims:
            </p>
            <ul className="space-y-2">
              <li><strong>Email:</strong> <a href="mailto:info@aureum7trading.com" className="text-primary hover:underline">info@aureum7trading.com</a></li>
              <li><strong>Phone:</strong> <a href="tel:+34642278939" className="text-primary hover:underline">+34 642 278 939</a></li>
              <li><strong>WhatsApp:</strong> +34 642 278 939</li>
              <li><strong>Business Hours:</strong> Monday-Friday, 9:00-18:00 CET</li>
              <li><strong>Address:</strong> Aureum VII Trading, Marbella, Andalusia, Spain</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">13. Governing Law</h2>
            <p>
              This Return & Refund Policy is governed by the laws of Spain and the European Union. 
              Any disputes arising from returns or refunds shall be subject to the jurisdiction of 
              the courts of Andalusia, Spain.
            </p>
          </section>

          <section className="border-t pt-6">
            <p className="text-sm text-muted-foreground">
              Aureum VII Trading reserves the right to update this Return & Refund Policy at any time. 
              Changes will be effective immediately upon posting on our website. Your continued use of 
              our services after changes constitutes acceptance of the revised policy.
            </p>
          </section>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default ReturnPolicy;
